import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LogoBicolor } from '../components/LogoBicolor';
import { Building2, MapPin, FileText, CheckCircle, Moon, Sun, Phone } from 'lucide-react';
import { toast } from 'sonner';

const specializations = [
  { value: 'orthopedics', label: 'Ortopedia' },
  { value: 'neurology', label: 'Neurologia' },
  { value: 'respiratory', label: 'Respiratória' },
  { value: 'sports', label: 'Esportiva' },
  { value: 'pediatrics', label: 'Pediátrica' },
  { value: 'geriatrics', label: 'Geriátrica' },
  { value: 'other', label: 'Outras' }
];

export default function OnboardingPage() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    specializations: [],
    phone: '',
    clinic_name: '',
    clinic_address: '',
    clinic_cnpj: ''
  });
  const [loading, setLoading] = useState(false);
  const { updateUser } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();

  // Format phone number as Brazilian format: +55 (XX) 9XXXX-XXXX
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Limit to 13 digits (55 + DDD + 9 digits)
    const limited = digits.slice(0, 13);
    
    // Format the number
    if (limited.length === 0) return '';
    if (limited.length <= 2) return `+${limited}`;
    if (limited.length <= 4) return `+${limited.slice(0, 2)} (${limited.slice(2)}`;
    if (limited.length <= 5) return `+${limited.slice(0, 2)} (${limited.slice(2, 4)}) ${limited.slice(4)}`;
    if (limited.length <= 9) return `+${limited.slice(0, 2)} (${limited.slice(2, 4)}) ${limited.slice(4, 5)}${limited.slice(5)}`;
    return `+${limited.slice(0, 2)} (${limited.slice(2, 4)}) ${limited.slice(4, 5)}${limited.slice(5, 9)}-${limited.slice(9)}`;
  };

  // Convert formatted phone to E.164 format for storage
  const phoneToE164 = (formatted) => {
    const digits = formatted.replace(/\D/g, '');
    return digits.length >= 12 ? `+${digits}` : '';
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({
      ...formData,
      phone: formatted
    });
  };

  const handleSpecializationToggle = (spec) => {
    setFormData((prev) => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter((s) => s !== spec)
        : [...prev.specializations, spec]
    }));
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleNext = () => {
    if (step === 1 && formData.specializations.length === 0) {
      toast.error('Selecione pelo menos uma especialização');
      return;
    }
    setStep(step + 1);
  };

  const handleFinish = async () => {
    setLoading(true);
    try {
      // Convert phone to E.164 format before saving
      const dataToSave = {
        ...formData,
        phone: phoneToE164(formData.phone) || null,
        onboarding_completed: true
      };
      await updateUser(dataToSave);
      toast.success('Perfil configurado com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    try {
      await updateUser({
        onboarding_completed: true
      });
      toast.success('Onboarding concluído!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Erro ao finalizar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-slate-900' : 'bg-white'} relative flex items-center justify-center px-4 py-12`}>
      {/* Theme Toggle */}
      <div className="absolute top-4 right-4 z-20">
        <Button
          onClick={toggleTheme}
          variant="ghost"
          size="icon"
          className={isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}
          data-testid="theme-toggle-btn"
        >
          {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
      </div>

      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src={isDark 
            ? "https://customer-assets.emergentagent.com/job_physio-scribe/artifacts/yw5h8o7l_Gemini_Generated_Image_75um2875um2875um.png"
            : "https://customer-assets.emergentagent.com/job_physio-scribe/artifacts/8vc22kdd_Gemini_Generated_Image_5_Fundo%20Claro.png"
          }
          alt="Tecnologia médica"
          className={`w-full h-full object-cover ${isDark ? 'opacity-30' : 'opacity-15'}`}
        />
        <div className={`absolute inset-0 ${isDark 
          ? 'bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-900/90'
          : 'bg-gradient-to-br from-white/90 via-white/80 to-white/90'}`} 
        />
      </div>

      <div className="relative z-10 max-w-2xl w-full">
        <div className={`${isDark ? 'bg-slate-900/80 border-slate-800' : 'bg-white/80 border-slate-200'} backdrop-blur-xl rounded-2xl shadow-2xl border p-8`}>
          <div className="flex items-center justify-center mb-8">
            <LogoBicolor size={28} symbolSize={44} />
          </div>

          {/* Progress */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3].map((num) => (
              <div key={num} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
                    step >= num
                      ? 'bg-primary text-white'
                      : isDark ? 'bg-slate-800 text-slate-400' : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {step > num ? <CheckCircle className="h-5 w-5" /> : num}
                </div>
                {num < 3 && (
                  <div
                    className={`w-16 h-1 mx-2 ${
                      step > num ? 'bg-primary' : isDark ? 'bg-slate-800' : 'bg-slate-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Specializations */}
          {step === 1 && (
            <div data-testid="onboarding-step-1">
              <h2 className={`text-2xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'} text-center mb-2`}>
                Suas especializações
              </h2>
              <p className={`${isDark ? 'text-slate-400' : 'text-slate-600'} text-center mb-8`}>
                Selecione suas áreas de atuação
              </p>

              <div className="grid grid-cols-2 gap-3 mb-8">
                {specializations.map((spec) => (
                  <button
                    key={spec.value}
                    type="button"
                    onClick={() => handleSpecializationToggle(spec.value)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      formData.specializations.includes(spec.value)
                        ? 'border-primary bg-primary/10'
                        : isDark 
                          ? 'border-slate-700 bg-slate-950/50 hover:border-slate-600'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                    data-testid={`specialization-${spec.value}`}
                  >
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{spec.label}</span>
                  </button>
                ))}
              </div>

              <Button
                onClick={handleNext}
                className="w-full bg-gradient-akali hover:opacity-90 text-white"
                data-testid="onboarding-next-btn"
              >
                Continuar
              </Button>
            </div>
          )}

          {/* Step 2: Clinic Info */}
          {step === 2 && (
            <div data-testid="onboarding-step-2">
              <h2 className={`text-2xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'} text-center mb-2`}>
                Dados da clínica
              </h2>
              <p className={`${isDark ? 'text-slate-400' : 'text-slate-600'} text-center mb-8`}>
                Informações opcionais para personalização
              </p>

              <div className="space-y-6 mb-8">
                {/* WhatsApp Field */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      WhatsApp (opcional)
                    </div>
                  </Label>
                  <div className="relative">
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="+55 (11) 99999-9999"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      className={`${isDark 
                        ? 'bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500' 
                        : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'}`}
                      data-testid="phone-input"
                    />
                  </div>
                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                    Receba alertas de uso quando atingir 70% ou 100% do limite de consultas
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clinic_name" className={isDark ? 'text-slate-300' : 'text-slate-700'}>Nome da Clínica (opcional)</Label>
                  <div className="relative">
                    <Building2 className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                    <Input
                      id="clinic_name"
                      name="clinic_name"
                      type="text"
                      placeholder="Clínica de Fisioterapia"
                      value={formData.clinic_name}
                      onChange={handleChange}
                      className={`pl-10 ${isDark 
                        ? 'bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500' 
                        : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'}`}
                      data-testid="clinic-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clinic_address" className={isDark ? 'text-slate-300' : 'text-slate-700'}>Endereço (opcional)</Label>
                  <div className="relative">
                    <MapPin className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                    <Input
                      id="clinic_address"
                      name="clinic_address"
                      type="text"
                      placeholder="Rua, número - Cidade, Estado"
                      value={formData.clinic_address}
                      onChange={handleChange}
                      className={`pl-10 ${isDark 
                        ? 'bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500' 
                        : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'}`}
                      data-testid="clinic-address-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clinic_cnpj" className={isDark ? 'text-slate-300' : 'text-slate-700'}>CNPJ (opcional)</Label>
                  <div className="relative">
                    <FileText className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                    <Input
                      id="clinic_cnpj"
                      name="clinic_cnpj"
                      type="text"
                      placeholder="00.000.000/0000-00"
                      value={formData.clinic_cnpj}
                      onChange={handleChange}
                      className={`pl-10 ${isDark 
                        ? 'bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500' 
                        : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'}`}
                      data-testid="clinic-cnpj-input"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className={`flex-1 ${isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : ''}`}
                  data-testid="onboarding-back-btn"
                >
                  Voltar
                </Button>
                <Button
                  onClick={handleNext}
                  className="flex-1 bg-primary hover:bg-primary-hover"
                  data-testid="onboarding-next-btn"
                >
                  Continuar
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Welcome */}
          {step === 3 && (
            <div className="text-center" data-testid="onboarding-step-3">
              <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-8 w-8 text-primary" />
              </div>
              <h2 className={`text-2xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-4`}>
                Tudo pronto!
              </h2>
              <p className={`${isDark ? 'text-slate-400' : 'text-slate-600'} mb-8 max-w-md mx-auto`}>
                Sua conta está configurada. Agora você pode começar a documentar suas consultas de forma inteligente.
              </p>

              <div className={`${isDark ? 'bg-primary/10' : 'bg-primary-light'} rounded-lg p-6 mb-8`}>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-3`}>Próximos passos:</h3>
                <ul className={`text-left space-y-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Adicione seus primeiros pacientes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Grave sua primeira consulta</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Deixe a IA gerar sua documentação</span>
                  </li>
                </ul>
              </div>

              <Button
                onClick={handleFinish}
                className="w-full bg-primary hover:bg-primary-hover"
                disabled={loading}
                data-testid="onboarding-finish-btn"
              >
                {loading ? 'Finalizando...' : 'Começar a usar'}
              </Button>
              
              <Button
                onClick={handleSkip}
                variant="ghost"
                className={`w-full mt-3 ${isDark ? 'text-slate-400 hover:text-white' : ''}`}
                disabled={loading}
                data-testid="onboarding-skip-btn"
              >
                Pular configuração
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
